import { useMemo, useState } from 'react'
import { calculateCompleteness } from '../../utils/cvCompleteness'
import { useTranslation } from '../../contexts/LanguageContext'
import type { CV } from '../../types/cv'

interface CVCompletenessIndicatorProps {
  cv: CV
}

export function CVCompletenessIndicator({ cv }: CVCompletenessIndicatorProps) {
  const { t } = useTranslation()
  const { score, sections } = useMemo(() => calculateCompleteness(cv), [cv])
  const [showTooltip, setShowTooltip] = useState(false)

  const labelMap: Record<string, string> = {
    name: t.completeness.fullName,
    title: t.completeness.professionalTitle,
    contact: t.completeness.contactInfo,
    summary: t.completeness.professionalSummary,
    experience: t.completeness.workExperience,
    education: t.completeness.education,
    skills: t.completeness.skills,
    languages: t.completeness.languages,
  }

  const getColor = () => {
    if (score >= 80) return 'text-emerald-600'
    if (score >= 50) return 'text-amber-600'
    return 'text-slate-400'
  }

  const getTrackColor = () => {
    if (score >= 80) return 'stroke-emerald-500'
    if (score >= 50) return 'stroke-amber-500'
    return 'stroke-slate-300'
  }

  // SVG circle progress
  const radius = 14
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  return (
    <div className="relative">
      <button
        onClick={() => setShowTooltip(!showTooltip)}
        onBlur={() => setShowTooltip(false)}
        className={`flex items-center gap-2 text-xs font-medium ${getColor()} hover:opacity-80 transition-opacity`}
        title={`CV ${score}%`}
      >
        <svg width="32" height="32" viewBox="0 0 32 32" className="-rotate-90">
          <circle
            cx="16"
            cy="16"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            opacity="0.15"
          />
          <circle
            cx="16"
            cy="16"
            r={radius}
            fill="none"
            className={getTrackColor()}
            strokeWidth="3"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <span className="absolute left-0 w-8 text-center text-[10px] font-bold">
          {score}
        </span>
      </button>

      {/* Tooltip dropdown */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg p-3 z-50">
          <p className="text-xs font-semibold text-slate-700 mb-2">
            {t.completeness.title}
          </p>
          <div className="space-y-1.5">
            {sections.map((section) => (
              <div key={section.id} className="flex items-center gap-2 text-xs">
                {section.completed ? (
                  <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                )}
                <span className={section.completed ? 'text-slate-600' : 'text-slate-400'}>
                  {labelMap[section.id] || section.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
